import { useState } from "react";

import { api, type SafeUser } from "../lib/api";

interface SetupPageProps {
  onComplete: () => void;
}

export function SetupPage({ onComplete }: SetupPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post<SafeUser>("/api/v1/setup", { username, password });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen w-screen place-items-center bg-paper px-6 py-12">
      <div className="relative w-full max-w-lg">
        <div className="absolute inset-x-0 -top-32 -z-10 h-40 grid-hero opacity-60" />
        <div className="card p-10">
          <Logomark />
          <div className="eyebrow mt-4">First-run setup</div>
          <h1 className="mt-2 font-display text-[32px] font-semibold leading-tight tracking-tightest text-ink">
            Create the administrator
          </h1>
          <p className="mt-3 text-[14px] leading-relaxed text-ash">
            One account is all the agent needs to start. You'll be able to add
            more users from the dashboard once you sign in.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Username
              </span>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoFocus
                required
                className="input"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                className="input"
              />
              <span className="mt-1.5 block text-[11.5px] text-ash">
                Twelve characters minimum, scored ≥ 3 by zxcvbn — long
                unusual phrases beat short complicated ones.
              </span>
            </label>
            <label className="block">
              <span className="mb-1.5 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-ash">
                Confirm password
              </span>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="input"
              />
            </label>

            {error ? (
              <div className="rounded-md border border-rose-500/30 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full justify-center py-2.5"
            >
              {busy ? "Creating account…" : "Create administrator →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Logomark() {
  return (
    <div className="inline-flex items-center gap-3">
      <svg width="32" height="32" viewBox="0 0 28 28" fill="none" aria-hidden>
        <rect x="0.5" y="0.5" width="27" height="27" rx="6" stroke="#3a5fef" />
        <path
          d="M7 19V9h2v8h6v2H7Zm10-2.2 4-7.8h2.2L18 19h-1.6l-3-7h-.2l1.8 7H13l-3-10h2.2l1.8 7Z"
          fill="#5d83ff"
        />
      </svg>
      <div>
        <div className="font-display text-[15px] font-semibold tracking-tight text-ink">
          Litmus
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">
          Catalog Agent
        </div>
      </div>
    </div>
  );
}
